import React, { useState, useEffect, useRef } from 'react';

import { useResizeObserver } from 'usehooks-ts';

import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { Input, Table, Tag, Select, Tooltip, Flex, Button, Popconfirm, DatePicker, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';

import { LoadingOutlined, ReloadOutlined, PlusOutlined, DeleteFilled, EditFilled } from '@ant-design/icons';

import LoadingPlan from '@flc/components/loading-plans/loading-plan';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { exception } from '@extensions/notification';
import { userLoaded, setFilter } from '@store/actions';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { delayAction, getDate, getEnumList } from '@extensions/utils';

import { ILoadingPlanFilter } from '@entities/loading-plan-filter';
import { ILoadingPlan } from '@entities/loading-plan';
import { ICountry } from '@entities/country';
import { IUserSession } from '@entities/user-session';

import { LoadingPlanStatus, enumLabel as statusLabel } from '@enums/loading-plan-status';
import { IEnumItem } from '@enums/enum-item';

import { serverFetch } from '@flc/server';

dayjs.extend(utc);

const filterContext: string = 'LoadingPlans';

const LoadingPlans = () => {
    const { RangePicker } = DatePicker;

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [api, contextHolder] = notification.useNotification();

    const initFilter: ILoadingPlanFilter = {
        userId: userSession.userId,
        statuses: [LoadingPlanStatus.New],
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const { width = 0, height = 0 } = useResizeObserver({
        ref: containerRef,
        box: 'border-box',
    });

    const filterRef = useRef<HTMLDivElement>(null);
    const { width: filterWidth = 0, height: filterHeight = 0 } = useResizeObserver({
        ref: filterRef,
        box: 'border-box',
    });

    const filter = useAppSelector<ILoadingPlanFilter>((s) => s.filters[filterContext]);

    const [plans, setPlans] = useState<Array<ILoadingPlan>>();
    const [currentPlan, setCurrentPlan] = useState<ILoadingPlan>();

    const [loading, setLoading] = useState<boolean>(false);
    const [showFilter, setShowFilter] = useState<boolean>(true);

    const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
    const [openPlan, setOpenPlan] = useState<boolean>(false);

    const [countries, setCountries] = useState<Array<ICountry>>([]);

    const [statuses] = useState<Array<IEnumItem>>(getEnumList(LoadingPlanStatus, statusLabel));

    useEffect(() => {
        if (!refreshRequired) return;

        let cleanup = false;
        setLoading(true);

        const fetchData = async () => {
            const promises = [
                await serverFetch(`loadingplans`, { method: 'GET', queryParams: filter })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения плана погрузок', ex, () => d(userLoaded(undefined)));
                    }),
                await serverFetch(`countries`, { method: 'GET' })
                    .then((data: any) => {
                        return data;
                    })
                    .catch((ex: any) => {
                        exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                    }),
            ];

            Promise.all([promises]).then((result) => {
                if (cleanup) return;

                setPlans(result[0][0]);
                setCountries((result[0][1] || []).filter((c: ICountry) => c.generateTariff));

                setCurrentPlan(undefined);
                setRefreshRequired(false);

                setLoading(false);
            });
        };

        fetchData();
        return () => {
            cleanup = true;
        };
    }, [refreshRequired]);

    useEffect(() => {
        if (!filter) {
            d(setFilter(initFilter, filterContext));
            return;
        }

        delayAction(() => setRefreshRequired(true));
    }, [filter]);

    const onDelete = (id: string | undefined) => {
        if (!id) return;

        serverFetch(`loadingplans/${id}`, { method: 'DELETE' })
            .then(() => {
                setRefreshRequired(true);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка удаления плана погрузки', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        label: 'Добавить план погрузки',
                        key: 'add',
                        disabled: loading,
                        icon: <PlusOutlined />,
                        onClick: () => {
                            setCurrentPlan(undefined);
                            setOpenPlan(true);
                        },
                    },
                ]}
                filter={{
                    active: showFilter,
                    onHideFilter: () => {
                        setShowFilter(!showFilter);
                    },
                }}
            />
        );
    };

    const renderFilter = () => {
        return (
            <div ref={filterRef}>
                <Filter
                    display={showFilter}
                    items={[
                        <Input
                            key='markingCode'
                            style={{ width: 130 }}
                            placeholder='Маркировка'
                            value={filter?.markingCode}
                            onChange={(data) => {
                                d(setFilter({ ...filter, markingCode: data.target.value }, filterContext));
                            }}
                        />,
                        <Select
                            key='status'
                            placeholder='Статус'
                            value={filter?.statuses}
                            size='middle'
                            mode='multiple'
                            maxTagCount='responsive'
                            allowClear
                            style={{ width: 230 }}
                            onChange={(value) => d(setFilter({ ...filter, statuses: value }, filterContext))}
                            options={statuses.map((s) => {
                                return { value: s.value, label: s.label };
                            })}
                        />,
                        <Select
                            key='country'
                            placeholder='Страна'
                            value={filter?.countryId}
                            size='middle'
                            allowClear={true}
                            style={{ width: 200 }}
                            onChange={(value) => d(setFilter({ ...filter, countryId: value }, filterContext))}
                            showSearch={{
                                filterOption: (input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase()),
                                filterSort: (a, b) => (a?.label as string).toLowerCase().localeCompare((b?.label as string).toLowerCase()),
                            }}
                            options={countries.map((c) => {
                                return { value: c.id, label: c.name };
                            })}
                        />,
                        <RangePicker
                            style={{ width: 220 }}
                            allowEmpty={[true, true]}
                            key='departure'
                            format='DD.MM.YYYY'
                            placeholder={['Погрузка с', 'до']}
                            cellRender={(current) => {
                                return <div className='ant-picker-cell-inner'>{(current as Dayjs).date()}</div>;
                            }}
                            value={[
                                filter?.plannedFrom ? dayjs(filter?.plannedFrom) : null,
                                filter?.plannedTo ? dayjs(filter?.plannedTo) : null,
                            ]}
                            onChange={(value) => {
                                if (!value) {
                                    d(
                                        setFilter(
                                            {
                                                ...filter,
                                                plannedFrom: undefined,
                                                plannedTo: undefined,
                                            },
                                            filterContext,
                                        ),
                                    );

                                    return;
                                }

                                d(
                                    setFilter(
                                        {
                                            ...filter,
                                            plannedFrom: value[0]
                                                ? dayjs(value[0]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                            plannedTo: value[1]
                                                ? dayjs(value[1]).utc(true).set('hour', 0).set('minute', 0).set('second', 0).toString()
                                                : undefined,
                                        },
                                        filterContext,
                                    ),
                                );
                            }}
                        />,
                    ]}
                    onClear={() => d(setFilter(initFilter, filterContext))}
                />
            </div>
        );
    };

    const renderTable = () => {
        const columns: ColumnsType<ILoadingPlan> = [
            {
                title: '',
                width: 60,
                render: (_, record) => {
                    return (
                        <Flex>
                            <Tooltip title='Изменить план погрузки'>
                                <Button
                                    icon={<EditFilled />}
                                    variant='link'
                                    color='blue'
                                    onClick={() => {
                                        setCurrentPlan(record);
                                        setOpenPlan(true);
                                    }}
                                />
                            </Tooltip>
                            <Popconfirm title='Удалить выбранный план погрузки?' onConfirm={() => onDelete(record.id)}>
                                <Tooltip title='Удалить план погрузки'>
                                    <Button
                                        icon={<DeleteFilled />}
                                        variant='link'
                                        color='blue'
                                        disabled={record.status != LoadingPlanStatus.New}
                                    />
                                </Tooltip>
                            </Popconfirm>
                        </Flex>
                    );
                },
            },
            {
                title: 'Маркировка',
                width: 200,
                dataIndex: 'markingCode',
            },
            {
                title: 'Страна',
                width: 110,
                dataIndex: 'countryName',
            },
            {
                title: 'Продукт',
                width: 190,
                align: 'center',
                render: (_, record) => {
                    return (
                        <Tag
                            color={record.itemColor ?? 'red'}
                            style={{
                                marginBottom: 2,
                                paddingBottom: 2,
                                width: 'fit-content',
                                fontSize: 15,
                                fontWeight: 600,
                            }}
                        >
                            {record.itemName ?? '- Не задан -'}
                        </Tag>
                    );
                },
            },
            {
                title: 'AWB / Торговая площадка',
                align: 'center',
                dataIndex: 'awbNumber',
                width: 200,
            },
            {
                title: 'Кол-во / шт',
                align: 'center',
                dataIndex: 'qty',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: 'Брутто / кг',
                align: 'center',
                dataIndex: 'grossWeight',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: 'Объемный / кг',
                align: 'center',
                dataIndex: 'volumeWeight',
                width: 120,
                onCell: () => ({
                    style: {
                        background: '#fafafa',
                        fontWeight: 600,
                    },
                }),
            },
            {
                title: 'Планируемая дата',
                width: 140,
                align: 'center',
                render: (_, record) => {
                    return getDate(record.plannedDate);
                },
            },
            {
                title: 'Статус',
                width: 120,
                align: 'center',
                render: (_, record) => {
                    return statusLabel(record.status);
                },
            },
            {},
        ];

        return (
            <Table
                rowKey='id'
                size='small'
                columns={columns}
                dataSource={plans}
                loading={{
                    spinning: loading,
                    indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
                }}
                pagination={false}
                scroll={{ y: height, x: 'max-content' }}
            />
        );
    };

    return (
        <div ref={containerRef} style={{ height: `calc(100vh - 295px - ${filterHeight}px)` }}>
            {renderToolbar()}
            {renderFilter()}
            {renderTable()}

            {openPlan && (
                <LoadingPlan
                    planId={currentPlan?.id}
                    baseCountries={countries}
                    userSession={userSession}
                    api={api}
                    onSave={() => {
                        setOpenPlan(false);
                        setRefreshRequired(true);
                    }}
                    onCancel={() => {
                        setOpenPlan(false);
                    }}
                    serverFetch={serverFetch}
                />
            )}

            {contextHolder}
        </div>
    );
};

export default LoadingPlans;
